import React from "react";

/**
 * Watches the device GPS while active=true and pushes the position
 * to the backend via the provided uploader callback.
 *
 * - High accuracy
 * - Throttles uploads to once every `intervalMs` (default 15s)
 * - Returns { position, error, supported, online, last_sent_at }
 */
export function useGeolocationWatch({ active, uploader, intervalMs = 15000 }) {
  const [position, setPosition] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [supported, setSupported] = React.useState(true);
  const [lastSentAt, setLastSentAt] = React.useState(null);
  const lastUploadRef = React.useRef(0);
  const watchIdRef = React.useRef(null);

  React.useEffect(() => {
    if (!active) {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      return;
    }
    if (!("geolocation" in navigator)) {
      setSupported(false);
      return;
    }
    setSupported(true);

    const onPos = (pos) => {
      setPosition(pos);
      setError(null);
      const now = Date.now();
      if (now - lastUploadRef.current >= intervalMs) {
        lastUploadRef.current = now;
        const payload = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy_m: pos.coords.accuracy,
          heading: pos.coords.heading,
          speed_mps: pos.coords.speed,
        };
        uploader(payload)
          .then(() => setLastSentAt(new Date().toISOString()))
          .catch(() => {});
      }
    };
    const onErr = (err) => {
      setError(err.message || "GPS-fejl");
    };

    watchIdRef.current = navigator.geolocation.watchPosition(onPos, onErr, {
      enableHighAccuracy: true,
      maximumAge: 5000,
      timeout: 20000,
    });

    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, [active, intervalMs, uploader]);

  return { position, error, supported, lastSentAt };
}
